import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { websiteAPI } from '../../../services/api';

/**
 * Block Picker Modal — opens when the admin clicks "+ Add Section" in the
 * builder. Lists all available block types categorised, with search.
 *
 * Props:
 *   open     — boolean
 *   onClose  — () => void
 *   onPick   — (blockType: string) => void  // caller inserts the new block
 *
 * The catalog is fetched once and cached in state for the modal lifetime.
 */
export default function BlockPickerModal({ open, onClose, onPick }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('hero');
  const [search, setSearch] = useState('');

  // Lazy-load the catalog the first time the modal opens.
  useEffect(() => {
    if (!open || catalog) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const resp = await websiteAPI.blockLibrary();
        if (cancelled) return;
        const body = resp?.data?.data || resp?.data;
        setCatalog(body);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'تعذّر تحميل المكتبة');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, catalog]);

  // ESC closes; lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Flatten matching blocks. When the user types a search query, ignore the
  // active category filter — searching is global by design (otherwise users
  // miss matches sitting in other tabs and get confused).
  const filteredBlocks = useMemo(() => {
    if (!catalog?.blocks) return [];
    const q = search.trim().toLowerCase();
    const isSearching = q.length > 0;
    return Object.entries(catalog.blocks)
      .map(([type, b]) => ({ type, ...b }))
      .filter((b) => !b.deprecated)
      .filter((b) => isSearching || activeCategory === 'all' || b.category === activeCategory)
      .filter((b) => {
        if (!q) return true;
        const labels = [b.label?.ar, b.label?.en, b.description?.ar, b.description?.en, b.type]
          .filter(Boolean).join(' ').toLowerCase();
        return labels.includes(q);
      });
  }, [catalog, activeCategory, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" dir="rtl">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">إضافة قسم جديد</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">اختر النوع المناسب لمحتواك</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            title="إغلاق (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search + categories */}
        <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث في المكتبة..."
              className="w-full pr-9 pl-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400/20 focus:border-blue-400"
            />
          </div>
          {catalog?.categories && (
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveCategory('all')}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold whitespace-nowrap transition-colors ${activeCategory === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
              >الكل</button>
              {catalog.categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCategory(c.id)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold whitespace-nowrap transition-colors ${activeCategory === c.id ? 'bg-blue-600 text-white' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                >
                  {c.label?.ar || c.id}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          )}
          {error && (
            <div className="text-center py-16">
              <p className="text-sm text-red-600 font-semibold mb-2">{error}</p>
              <button
                type="button"
                onClick={() => { setCatalog(null); }}
                className="text-[12px] text-blue-600 hover:underline"
              >إعادة المحاولة</button>
            </div>
          )}
          {!loading && !error && search.trim() && filteredBlocks.length > 0 && (
            <div className="mb-3 text-[11px] text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
              <Search size={11} /> البحث يتجاوز التصنيف الحالي — يبحث في كل المكتبة
            </div>
          )}
          {!loading && !error && filteredBlocks.length === 0 && (
            <div className="text-center py-16">
              <p className="text-sm text-slate-400 mb-3">لا توجد نتائج تطابق بحثك</p>
              {(search.trim() || activeCategory !== 'all') && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setActiveCategory('all'); }}
                  className="text-[12px] text-blue-600 hover:underline"
                >إعادة تعيين الفلاتر</button>
              )}
            </div>
          )}
          {!loading && !error && filteredBlocks.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBlocks.map((b) => (
                <button
                  key={b.type}
                  type="button"
                  onClick={() => onPick?.(b.type)}
                  className="group bg-white border border-slate-200 rounded-xl p-4 text-right hover:border-blue-400 hover:shadow-md transition-all duration-200 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
                      {b.label?.ar || b.type}
                    </h3>
                    <span className="text-[10px] text-slate-400 font-mono px-1.5 py-0.5 bg-slate-50 rounded border border-slate-100" dir="ltr">
                      {b.type}
                    </span>
                  </div>
                  {b.description?.ar && (
                    <p className="text-[12px] text-slate-500 leading-relaxed">
                      {b.description.ar}
                    </p>
                  )}
                  <div className="mt-auto pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-blue-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                      اضغط لإضافة هذا القسم →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
